
import styles from "./WrappingFeeDialog.module.scss";
import { DeliveryOption, WrappingSizeOption } from "../../../libs/types";
import { Button, Dialog, DialogContent, DialogTitle } from "@mui/material";
import React from "react";
import CloseIcon from '@mui/icons-material/Close';
import DateSelect from "../../core/DateSelect";
import InputGroup from "../../core/InputGroup";
import { str2number } from "../../../libs/helpers";
import { format } from "date-fns";
import { BrandlistResponseData } from "../../../apis/models/BrandlistResponse";
import { InvoiceListResponseData, InvoiceMasterData } from "../../../apis/models/InvoiceMasterRequest";
import DateWithoutTime from "../../../libs/dateWithoutTime";

type WrappingSizeType = {
  wrappingSizeOptionId: string;
  quantity: string;
}

type DeliveryMethodType = {
  deliveryMethodOptionId: string;
  quantity: string;
}

export type WrappingFeeAddDialogData = {
  dateAdded: DateWithoutTime;
  remarks: string;
  differenceInSalesNumber: string;
  sizeList: WrappingSizeType[];
  deliveryList: DeliveryMethodType[];
}

type ErrorInfo = {
  dateAdded?: string;
  total?: string;
  compareSizeAndDelivery?: string;
  differenceInSalesNumber?: string;
  sizeErrors: WrappingSizeType[];
  deliveryErrors: DeliveryMethodType[];
}

type RowType = WrappingFeeAddDialogData & {
  error: ErrorInfo
}

const createInitialItem = () => {
  return {
    dateAdded: DateWithoutTime.now(),
    remarks: "",
    differenceInSalesNumber: "",
    sizeList: [],
    deliveryList: [],
    error: {
      sizeErrors: [],
      deliveryErrors: [],
    }
  } as RowType
}

const WrappingFeeDialog: React.FC<WrappingFeeDialogProps> = (props: WrappingFeeDialogProps) => {
  const [dialogData, setDialogData] = React.useState<RowType[]>([createInitialItem()]);

  const validateRow = (item: RowType) => {
    const errorSizeList = item.sizeList
      .map(element => {
        return {
          wrappingSizeOptionId: element.wrappingSizeOptionId,
          quantity: validateNumber(element.quantity),
        } as WrappingSizeType
      })
      .filter(fee => fee.quantity !== undefined)

    const errorDeliveryList = item.deliveryList
      .map(element => {
        return {
          deliveryMethodOptionId: element.deliveryMethodOptionId,
          quantity: validateNumber(element.quantity),
        } as DeliveryMethodType
      })
      .filter(fee => fee.quantity !== undefined)

    const sizeTotal = calculateSizeTotal(item)
    const deliveryTotal = calculateDeliveryTotal(item)

    const error: ErrorInfo = {
      dateAdded: validateDateAdded(item.dateAdded),
      total: sizeTotal <= 0 ? "個数を入力してください" : undefined,
      compareSizeAndDelivery: sizeTotal !== deliveryTotal ? "発送数量が合計数と一致していません" : undefined,
      differenceInSalesNumber: validateNumber(item.differenceInSalesNumber),
      sizeErrors: errorSizeList,
      deliveryErrors: errorDeliveryList,
    }
    return error
  }

  const validateDateAdded = (value: DateWithoutTime): string | undefined => {
    const dateString = value.getValue4Display()
    if (value.getValue4Display().length === 0) {
      return "入力必須です"
    } else if (value.isInvalid()) {
      return "年月日で入力してください"
    } else if (props.existDates.some(d => d.equals(value))) {
      return "すでに入力されている日付です"
    } else if (!props.targetInvoice && !getInvoiceAtDate(dateString)) {
      return "請求期間の範囲で入力してください"
    } else if (props.targetInvoice) {
      const invoiceFrom = format(new Date(props.targetInvoice.from), "yyyy/MM/dd")
      const invoiceTo = format(new Date(props.targetInvoice.to), "yyyy/MM/dd")
      if (dateString < invoiceFrom || invoiceTo < dateString) {
        return "請求期間の範囲で入力してください"
      }
    }
    return undefined
  }
  
  const validateNumber = (value: string) => {
    if (value.length === 0) {
      return undefined
    } else if (isNaN(+value) || parseInt(value) < 1) {
      return "１以上を入力してください"
    }
    return undefined
  }

  const getInvoiceAtDate = (value: string) => {
    if (!props.targetInvoices || props.targetInvoices.length <= 0) {
      return undefined
    }
    return props.targetInvoices.find(invoice => {
      const invoiceFrom = format(new Date(invoice.from), "yyyy/MM/dd")
      const invoiceTo = format(new Date(invoice.to), "yyyy/MM/dd")
      return (invoiceFrom <= value  && value <= invoiceTo)
    })
  }

  const calculateSizeTotal = (rowData: RowType) => {
    return rowData.sizeList.reduce((prev, element) => prev + str2number(element.quantity), 0)
  }

  const calculateDeliveryTotal = (rowData: RowType) => {
    return rowData.deliveryList.reduce((prev, element) => prev + str2number(element.quantity), str2number(rowData.differenceInSalesNumber))
  }

  const isDisabledSave = () => {
    if (dialogData.length <= 0 || duplicateDate.length > 0) {
      return true
    }
    return dialogData.some((item, index) => {
      const err = validateRow(item)
      item.error = err
      const newItems = [...dialogData]
      newItems.splice(index, 1, item)
      setDialogData(newItems)
      return (
        err.dateAdded ||
        err.total ||
        err.compareSizeAndDelivery ||
        err.differenceInSalesNumber ||
        err.sizeErrors.some(err => err.quantity) ||
        err.deliveryErrors.some(err => err.quantity)
      )
    })
  }

  const handleDateChange = (index: number, d: Date | null) => {
    const newItems = [...dialogData];
    const rowItem = newItems[index];
    rowItem.dateAdded = new DateWithoutTime(d);
    if (Object.keys(rowItem.error).length > 2 || rowItem.error.sizeErrors.length !== 0 || rowItem.error.deliveryErrors.length !== 0) {
      rowItem.error = validateRow(rowItem)
    }
    newItems.splice(index, 1, rowItem);
    setDialogData(newItems);
  }

  const handleSizeInputChange = (index: number, sizeId: string, value: string) => {
    const newItems = [...dialogData];
    const rowItem = newItems[index];
    const sizeData = rowItem.sizeList.find(element => element.wrappingSizeOptionId === sizeId)
    if (!sizeData) {
      rowItem.sizeList.push({
        wrappingSizeOptionId: sizeId,
        quantity: value,
      });
    } else {
      sizeData.quantity = value;
    }

    const {calcValue, defaultOptionId} = calculateDefaultDelivery(rowItem);
    if (defaultOptionId) {
      const deliveryData = rowItem.deliveryList.find(element => element.deliveryMethodOptionId === defaultOptionId)
      if (!deliveryData) {
        rowItem.deliveryList.push({
          deliveryMethodOptionId: defaultOptionId,
          quantity: calcValue + "",
        });
      } else {
        deliveryData.quantity = calcValue + "";
      }
    }
    if (Object.keys(rowItem.error).length > 2 || rowItem.error.sizeErrors.length !== 0 || rowItem.error.deliveryErrors.length !== 0) {
      rowItem.error = validateRow(rowItem)
    }
    newItems.splice(index, 1, rowItem);
    setDialogData(newItems);
  }

  const handleDeliveryInputChange = (index: number, deliveryId: string, value: string) => {
    const newItems = [...dialogData];
    const rowItem = newItems[index];
    const deliveryData = rowItem.deliveryList.find(element => element.deliveryMethodOptionId === deliveryId)
    if (!deliveryData) {
      rowItem.deliveryList.push({
        deliveryMethodOptionId: deliveryId,
        quantity: value,
      });
    } else {
      deliveryData.quantity = value;
    }

    const {calcValue, defaultOptionId} = calculateDefaultDelivery(rowItem);
    if (defaultOptionId) {
      const deliveryData = rowItem.deliveryList.find(element => element.deliveryMethodOptionId === defaultOptionId)
      if (!deliveryData) {
        rowItem.deliveryList.push({
          deliveryMethodOptionId: defaultOptionId,
          quantity: calcValue + "",
        });
      } else {
        deliveryData.quantity = calcValue + "";
      }
    }

    if (Object.keys(rowItem.error).length > 2 || rowItem.error.sizeErrors.length !== 0 || rowItem.error.deliveryErrors.length !== 0) {
      rowItem.error = validateRow(rowItem)
    }
    newItems.splice(index, 1, rowItem);
    setDialogData(newItems);
  }

  const handleDifferenceInSalesNumberChange = (index: number, val: string) => {
    const newItems = [...dialogData];
    const rowItem = newItems[index];
    rowItem.differenceInSalesNumber = val;

    const {calcValue, defaultOptionId} = calculateDefaultDelivery(rowItem);
    if (defaultOptionId) {
      const deliveryData = rowItem.deliveryList.find(element => element.deliveryMethodOptionId === defaultOptionId)
      if (!deliveryData) {
        rowItem.deliveryList.push({
          deliveryMethodOptionId: defaultOptionId,
          quantity: calcValue + "",
        });
      } else {
        deliveryData.quantity = calcValue + "";
      }
    }

    if (Object.keys(rowItem.error).length > 2 || rowItem.error.sizeErrors.length !== 0 || rowItem.error.deliveryErrors.length !== 0) {
      rowItem.error = validateRow(rowItem)
    }
    newItems.splice(index, 1, rowItem);
    setDialogData(newItems);
  }

  const calculateDefaultDelivery = (rowData: RowType) => {
    const total = calculateSizeTotal(rowData);
    const deliverySize = calculateDeliveryTotal(rowData);
    const defaultOptionId = props.wrappingDeliveryOptions.find(element => element.defaultFlag === 1)?.deliveryMethodOptionId;
    const defaultQuantity = rowData.deliveryList.find(element => element.deliveryMethodOptionId === defaultOptionId)?.quantity;
    let calcValue: number | string = total - (deliverySize - str2number(defaultQuantity));
    calcValue = calcValue === 0 ? "" : calcValue;
    return {calcValue, defaultOptionId}
  }

  const handleRemarkChange = (index: number, val: string) => {
    const newItems = [...dialogData];
    const rowItem = newItems[index];
    rowItem.remarks = val;
    if (Object.keys(rowItem.error).length > 2 || rowItem.error.sizeErrors.length !== 0 || rowItem.error.deliveryErrors.length !== 0) {
      rowItem.error = validateRow(rowItem)
    }
    newItems.splice(index, 1, rowItem);
    setDialogData(newItems);
  }

  const getDateError = (rowData: RowType) => {
    if (rowData.error.dateAdded) {
      return rowData.error.dateAdded
    }
    if (duplicateDate.includes(rowData.dateAdded)) {
      return "日付が重複しています"
    }
    return undefined
  }

  const deleteAddingItem = (index: number) => {
    if (dialogData.length <= 1) {
      return
    }
    const copyDialogData = [...dialogData];
    copyDialogData.splice(index, 1);
    setDialogData(copyDialogData);
  }

  const addEmptyRow = () => {
    setDialogData([...dialogData, createInitialItem()]);
  }

  const saveAddedItem = () => {
    if (isDisabledSave()) {
      return;
    }
    dialogData.forEach(item => {
      item.sizeList = item.sizeList.filter(size => str2number(size.quantity) > 0);
      item.deliveryList = item.deliveryList.filter(size => str2number(size.quantity) > 0);
    })
    props.onSave(dialogData);
  }

  const closeDialog = () => {
    props.onClose();
  }

  const getSizeCellValue = (rowIndex: number, optionId: string) => {
    const rowData = dialogData[rowIndex];
    const fee = rowData.sizeList.find(element => element.wrappingSizeOptionId === optionId);
    return fee ? fee.quantity : ""
  }

  const getDeliveryCellValue = (rowIndex: number, optionId: string) => {
    const rowData = dialogData[rowIndex];
    const fee = rowData.deliveryList.find(element => element.deliveryMethodOptionId === optionId);
    return fee ? fee.quantity : ""
  }

  const sizeOptionMaster = React.useMemo(() => {
    return props.wrappingSizeOptions
      .sort((a,b) => (a.order ?? 0) - (b.order ?? 0))
      .map(o => {
        return {
          optionId: o.wrappingSizeOptionId!,
          wrappingSize: o.wrappingSizeOption.wrappingSize,
          unitPrice: o.wrappingSizeOption.wrappingSizeUnitPrice,
        }
      });
  }, [props.wrappingSizeOptions]);

  const deliveryOptionMaster = React.useMemo(() => {
    return props.wrappingDeliveryOptions
      .sort((a,b) => (a.order ?? 0) - (b.order ?? 0))
      .map(o => {
        return {
          optionId: o.deliveryMethodOptionId!,
          deliveryMethod: o.deliveryMethodOption.deliveryMethod,
          isDefault: o.defaultFlag,
        }
      });
  }, [props.wrappingDeliveryOptions]);

  const invoicePeriod = React.useMemo(() => {
    if (props.targetInvoices && props.targetInvoices.length > 0) {
      const min = props.targetInvoices.reduce((prev, i) => prev < i.from ? prev : i.from , "3000-12-31")
      const max = props.targetInvoices.reduce((prev, i) => prev > i.to ? prev : i.to , "")
      return `${format(new Date(min), "yyyy/MM/dd")} ～ ${format(new Date(max), "yyyy/MM/dd")}`
    } else if (props.targetInvoice){
      return `${format(new Date(props.targetInvoice.from), "yyyy/MM/dd")} ～ ${format(new Date(props.targetInvoice.to), "yyyy/MM/dd")}`
    }
    return ''
  }, [props.targetInvoices, props.targetInvoice])

  const duplicateDate = React.useMemo(() => {
    const dateList = new Set<DateWithoutTime>()
    dialogData.forEach(data => {
      if (dialogData.filter(d => d.dateAdded.equals(data.dateAdded)).length > 1) {
        dateList.add(data.dateAdded)
      }
    })
    return Array.from(dateList)
  }, [dialogData])

  return (
    <Dialog open={props.open} onClose={() => {closeDialog()}} fullWidth={true} maxWidth="lg" PaperProps={{sx: {height: '90%'}}}>
      <DialogTitle sx={{fontWeight: 700, fontSize: "24px", paddingLeft: "40px"}}>{props.targetBrand?.brandName ?? ''} - 資材費追加</DialogTitle>
      <DialogContent sx={{padding: "0 40px 24px", display: "flex", flexDirection: "column"}}>
      <div className={styles.dialog__label}>
        <span>請求期間：</span>
        <span className={styles.dialog__label_content}>{invoicePeriod}</span>
      </div>
      <div className={styles.dialog__content}>
        {
          dialogData.map((dialogItem, dialogItemIndex) => (
            <div className={styles.dialog__content_item} key={dialogItemIndex}>
              <div className={styles.dialog__content_item_total}>
                <DateSelect
                  selectedDate={dialogItem.dateAdded.getDate()}
                  handleDateChange={(date) => handleDateChange(dialogItemIndex, date)}
                  label="日付"
                  required 
                  errMess={getDateError(dialogItem)}
                />
                <InputGroup
                  label="合計"
                  type="text"
                  disabled
                  value={calculateSizeTotal(dialogItem)}
                  error={!!(dialogItem.error.total)}
                  errorMessage={dialogItem.error.total}
                />
                <CloseIcon className={styles.dialog__content_item_icon} onClick={() => deleteAddingItem(dialogItemIndex)}/>
              </div>
              <table className={styles.dialog__content_item_sizes}>
                <tbody>
                <tr>
                {
                  sizeOptionMaster.map((sizeItem, sizeIndex) => (
                    <td key={sizeIndex} className={styles.size_group_label}>
                      <div>
                        {sizeItem.wrappingSize}<br/>({sizeItem.unitPrice})
                      </div>
                    </td>
                  ))
                }
                </tr>
                <tr>
                {
                  sizeOptionMaster.map((sizeItem, sizeIndex) => (
                    <td key={sizeIndex} className={styles.size_group_input}>
                      <div>
                        <InputGroup
                          type="number"
                          value={getSizeCellValue(dialogItemIndex, sizeItem.optionId)}
                          onChange={(val) => handleSizeInputChange(dialogItemIndex, sizeItem.optionId, val)}
                          error={dialogItem.error.sizeErrors.some(f => f.wrappingSizeOptionId === sizeItem.optionId)}
                          errorMessage={dialogItem.error.sizeErrors.find(f => f.wrappingSizeOptionId === sizeItem.optionId)?.quantity}
                        />
                      </div>
                    </td>
                  ))
                }
                </tr>
                </tbody>
              </table>
              <div className={styles.dialog__content_item_deliverytitle}>発送方法</div>
              <table className={styles.dialog__content_item_deliveries}>
                <tbody>
                <tr>
                  {
                    deliveryOptionMaster.map((deliveryItem, deliveryIndex) => (
                      <td key={deliveryIndex} className={styles.delivery_group_label}>
                        <div className={deliveryItem.isDefault ? `${styles.default}` : ""}>
                          {deliveryItem.deliveryMethod}
                        </div>
                      </td>
                    ))
                  }
                  <td className={styles.delivery_group_label}>
                    <div>
                      販売による<br/>個口数差数
                    </div>
                  </td>
                  <td rowSpan={2} className={styles.delivery_group_split}>

                  </td>
                  <td className={styles.delivery_group_label_remarks}>
                    <div>
                      備考
                    </div>
                  </td>
                </tr>
                <tr>
                  {
                    deliveryOptionMaster.map((deliveryItem, deliveryIndex) => (
                      <td key={deliveryIndex} className={styles.delivery_group_input}>
                        <div>
                          <InputGroup
                            type="number"
                            value={getDeliveryCellValue(dialogItemIndex, deliveryItem.optionId)}
                            onChange={(val) => {handleDeliveryInputChange(dialogItemIndex, deliveryItem.optionId, val)}}
                            error={dialogItem.error.deliveryErrors.some(f => f.deliveryMethodOptionId === deliveryItem.optionId)}
                            errorMessage={dialogItem.error.deliveryErrors.find(f => f.deliveryMethodOptionId === deliveryItem.optionId)?.quantity}
                          />
                        </div>
                      </td>
                    ))
                  }
                  <td className={styles.delivery_group_input}>
                    <InputGroup
                      type="number"
                      value={dialogItem.differenceInSalesNumber}
                      onChange={(val) => handleDifferenceInSalesNumberChange(dialogItemIndex, val)}
                      error={!!(dialogItem.error.differenceInSalesNumber)}
                      errorMessage={dialogItem.error.differenceInSalesNumber}
                    />
                  </td>
                  <td className={styles.delivery_group_input_remarks}>
                    <InputGroup
                      type="text"
                      value={dialogItem.remarks}
                      onChange={(val) => handleRemarkChange(dialogItemIndex, val)}
                    />
                  </td>
                </tr>
                </tbody>
              </table>
              {
                dialogItem.error.compareSizeAndDelivery ? (
                  <div className={styles.descripency}>{dialogItem.error.compareSizeAndDelivery}</div>
                ) : (
                <></>
                )
              }
            </div>
          ))
        }
      </div>
      <div className={styles.dialog__add}>
        <Button variant="outlined" color="primary" onClick={() => addEmptyRow()}>＋行追加</Button>
      </div>
      <div className={styles.dialog__functions}>
        <Button variant="outlined" color="primary" onClick={() => {closeDialog()}}>キャンセル</Button>
        <Button variant="contained" color="primary" onClick={() => saveAddedItem()}>登録</Button>
      </div>
      </DialogContent>
    </Dialog>
  );
};

export default WrappingFeeDialog;

export type WrappingFeeDialogProps = {
  targetBrand?: BrandlistResponseData,
  targetInvoice?: InvoiceMasterData,
  targetInvoices?: InvoiceListResponseData[],
  open: boolean,
  onClose: () => void,
  onSave: (items: WrappingFeeAddDialogData[]) => void,
  wrappingSizeOptions: WrappingSizeOption[],
  wrappingDeliveryOptions: DeliveryOption[],
  existDates: DateWithoutTime[],
}